import React from 'react';
import { Link } from 'react-router-dom';
import AppDropdown from '../../../components/AppDropdown';
import { useCoursesEntryScreen } from './hooks/useCoursesEntryScreen';

function TextField({ label, value, onChange, placeholder, required = false, type = 'text' }) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
        {label}
        {required ? <span className="text-error"> *</span> : null}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border-0 bg-surface-container-low px-4 py-3.5 text-base text-on-surface outline-none placeholder:text-on-surface-variant transition-all focus:ring-2 focus:ring-primary/20"
      />
    </label>
  );
}

export default function CoursesEntryScreen() {
  const { state, actions } = useCoursesEntryScreen();

  return (
    <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-6 pb-8">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Masters</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Courses Master</span>
          </div>
          <h1 className="mt-2 text-4xl font-extrabold tracking-[-0.02em] text-on-surface">
            {state.isEditMode ? 'Edit Course' : 'Create Course'}
          </h1>
          <p className="mt-2 max-w-xl text-base text-on-surface-variant">
            Configure reference data for courses, degrees, academic departments, and institutional ownership.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/masters/courses/list"
            className="inline-flex items-center justify-center rounded-xl bg-secondary-container px-7 py-3 text-sm font-bold text-secondary hover:brightness-95 transition"
          >
            Discard
          </Link>
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving}
            className="inline-flex items-center justify-center rounded-xl bg-primary px-8 py-3 text-sm font-bold text-on-primary shadow-lg shadow-primary/20 hover:brightness-105 active:scale-[0.98] transition disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          >
            {state.success ? 'Success! ✓' : state.isSaving ? 'Saving...' : 'Save Course'}
          </button>
        </div>
      </section>

      {state.isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-14 text-center text-sm text-on-surface-variant">
          <div className="flex flex-col items-center justify-center gap-2">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span>Loading course details...</span>
          </div>
        </div>
      ) : (
        <section className="grid grid-cols-12 gap-6">
          <div className="col-span-12 space-y-6 lg:col-span-8">
            <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-8">
              <div className="mb-8 flex items-center gap-4">
                <div className="h-10 w-1.5 rounded-full bg-primary" />
                <h2 className="text-3xl font-bold text-on-surface">Course Identification</h2>
              </div>

              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <div className="md:col-span-2">
                  <TextField
                    label="Course Name"
                    required
                    value={state.form.name}
                    onChange={(val) => actions.patchForm('name', val)}
                    placeholder="e.g. Bachelor of Computer Applications"
                  />
                </div>

                <TextField
                  label="Short Name"
                  value={state.form.short_name}
                  onChange={(val) => actions.patchForm('short_name', val)}
                  placeholder="e.g. BCA"
                />

                <label className="grid gap-2">
                  <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Department
                  </span>
                  <AppDropdown
                    value={state.form.department_id}
                    onChange={(val) => actions.patchForm('department_id', val)}
                    options={state.departmentOptions}
                    placeholder="Select Department..."
                    triggerClassName="w-full rounded-xl border-0 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface"
                  />
                </label>

                <label className="grid gap-2">
                  <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Company / Institute
                  </span>
                  <AppDropdown
                    value={state.form.company_id}
                    onChange={(val) => actions.patchForm('company_id', val)}
                    options={state.companyOptions}
                    placeholder="Select Company..."
                    triggerClassName="w-full rounded-xl border-0 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface"
                  />
                </label>

                <div className="grid gap-2">
                  <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Operational Status
                  </span>
                  <div className="flex items-center rounded-xl bg-surface-container-low p-1">
                    <button
                      type="button"
                      onClick={() => actions.setOperationalStatus('active')}
                      className={`flex-1 rounded-lg px-3 py-2 text-sm font-bold transition-all cursor-pointer ${
                        state.operationalStatus === 'active'
                          ? 'bg-surface-container-lowest text-primary shadow-sm'
                          : 'text-on-surface-variant'
                      }`}
                    >
                      Active
                    </button>
                    <button
                      type="button"
                      onClick={() => actions.setOperationalStatus('inactive')}
                      className={`flex-1 rounded-lg px-3 py-2 text-sm font-bold transition-all cursor-pointer ${
                        state.operationalStatus === 'inactive'
                          ? 'bg-surface-container-lowest text-on-surface shadow-sm'
                          : 'text-on-surface-variant'
                      }`}
                    >
                      Archived
                    </button>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <aside className="col-span-12 space-y-6 lg:col-span-4">
            <article className="rounded-[2rem] border border-outline-variant/15 bg-surface-container-low p-6">
              <h3 className="text-base font-bold text-on-surface">Configuration Completion</h3>
              <div className="mt-4 flex items-center gap-4">
                <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <span className="text-lg font-black text-primary">{state.completionPercent}%</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-on-surface">Setup Progress</p>
                  <p className="text-xs text-on-surface-variant">Fill course details to complete setup.</p>
                </div>
              </div>
            </article>

            <article className="rounded-[2rem] border border-outline-variant/15 bg-surface-container-low p-6 space-y-4">
              <h3 className="text-base font-bold text-on-surface">Academic Flags</h3>

              <label className="flex items-center justify-between rounded-xl bg-surface-container-lowest px-4 py-3 cursor-pointer">
                <span className="text-sm font-semibold text-on-surface-variant">Show in Current Courses</span>
                <input
                  type="checkbox"
                  checked={state.form.show_current}
                  onChange={(e) => actions.patchForm('show_current', e.target.checked)}
                  className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary cursor-pointer"
                />
              </label>

              <label className="flex items-center justify-between rounded-xl bg-surface-container-lowest px-4 py-3 cursor-pointer">
                <span className="text-sm font-semibold text-on-surface-variant">Show in Previous Courses</span>
                <input
                  type="checkbox"
                  checked={state.form.show_previous}
                  onChange={(e) => actions.patchForm('show_previous', e.target.checked)}
                  className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary cursor-pointer"
                />
              </label>
            </article>
          </aside>
        </section>
      )}
    </div>
  );
}
